import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import profileServices from '@src/services/profile.services';
import { invalidateApiCache } from '@src/services/api';
import usersServices from '@src/services/users.services';
import groupsServices from '@src/services/groups.service';

export const getCurrentUser = createAsyncThunk(
  'getCurrentUser',
  async (body, { dispatch, rejectWithValue }) => {
    try {
      const response = await profileServices.getCurrentUser();
      if (response?.data?.id) {
        rejectWithValue({ message: '' });
      }
      return response;
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const currentUserUpdate = createAsyncThunk(
  'currentUserUpdate',
  async (body, { dispatch, rejectWithValue }) => {
    try {
      await profileServices.currentUserUpdate({ entity: { ...body } });
      invalidateApiCache('Users-getCurrentUser');
      return await profileServices.getCurrentUser();
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const reGmsSendOtp = createAsyncThunk('reGmsSendOtp', async (body, { rejectWithValue }) => {
  try {
    return await profileServices.reGmsSendOtp(body);
  } catch (error) {
    return rejectWithValue(error?.data);
  }
});

export const reConfirmGmsOtp = createAsyncThunk(
  'reConfirmGmsOtp',
  async (body, { rejectWithValue }) => {
    try {
      return await profileServices.reConfirmGmsOtp(body);
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const reEmailSendOtp = createAsyncThunk(
  'reEmailSendOtp',
  async (body, { rejectWithValue }) => {
    try {
      return await profileServices.reEmailSendOtp(body);
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const reConfirmEmailOtp = createAsyncThunk(
  'reConfirmEmailOtp',
  async (body, { rejectWithValue }) => {
    try {
      return await profileServices.reConfirmEmailOtp(body);
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const userContactLookup = createAsyncThunk(
  'userContactLookup',
  async (contactId, { getState, rejectWithValue }) => {
    try {
      const customerId = await getState()?.customer?.selectedCustomer?.customerId;
      let body = contactId || customerId;
      if (!body) {
        return rejectWithValue({
          message: 'Please select a customer',
        });
      }

      const result = await usersServices.getUserContactLookup({
        customerId: body,
        vouId: 1,
      });
      return result;
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const createUserAndRole = createAsyncThunk(
  'createUserAndRole',
  async (data, { rejectWithValue }) => {
    try {
      const result = await usersServices.createUserManuelAndRole(data);
      return result;
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const createUserAndRoleAdmin = createAsyncThunk(
  'createUserAndRoleAdmin',
  async (data, { rejectWithValue }) => {
    try {
      const result = await usersServices.createUserAndRole(data);
      return result;
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const updateUserAndRole = createAsyncThunk(
  'updateUserAndRole',
  async (data, { rejectWithValue }) => {
    try {
      const result = await usersServices.updateUserManuelAndRole(data);
      return result;
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const getUserInfo = createAsyncThunk(
  'getUserInfo',
  async (body, { dispatch, rejectWithValue }) => {
    try {
      const response = await usersServices.getUserInfo(body);
      return response;
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const getUserCheckedFunctions = createAsyncThunk(
  'getUserCheckedFunctions',
  async ({ body, pageNumber, pageSize }, { rejectWithValue }) => {
    try {
      const response = await groupsServices.getAllClaims({ body, pageNumber, pageSize });
      return response;
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const getListUserAddressLookup = createAsyncThunk(
  'getListUserAddressLookup',
  async ({ body, PageNumber, PageSize }, { rejectWithValue }) => {
    try {
      return await usersServices.getListUserAddressLookup({ body, PageNumber, PageSize });
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const getUserAuthorizationStatus = createAsyncThunk(
  'getUserAuthorizationStatus',
  async (_, { rejectWithValue }) => {
    try {
      return await usersServices.getUserAuthorizationStatus();
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const getUserAuthorizationStatusGift = createAsyncThunk(
  'getUserAuthorizationStatusGift',
  async (_, { rejectWithValue }) => {
    try {
      return await usersServices.getUserAuthorizationStatusGift();
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const getUserAuthorizationStatusMarket = createAsyncThunk(
  'getUserAuthorizationStatusMarket',
  async (_, { rejectWithValue }) => {
    try {
      return await usersServices.getUserAuthorizationStatusMarket();
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const getCheckList = createAsyncThunk(
  'admin/getCheckList',
  async (userId, { rejectWithValue }) => {
    try {
      return await groupsServices.getCheckList(userId);
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

const initialState = {
  userDetail: {},
  userInfo: {},
  currentUser: {},
  contactInfo: {},
  contactList: [],
  claims: [],
  addressList: [],
  userAuthorizationStatus: [],
  userAuthorizationGiftStatus: [],
  userAuthorizationMarketStatus: [],
  tempFormList: {},
  claimList: {},
  newClaimList: {},
  newAddressList: {},
  deletedClaims: {},
  deletedAddress: {},
  resGmsSendOtp: {},
  resConfirmGmsOtp: {},
  resEmailSendOtp: {},
  resConfirmEmailOtp: {},
  selectedAddressType: {},
  selectedUserId: false,
};

export const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    updateDetail(state, action) {
      state.userDetail = { ...state.userDetail, ...action.payload };
    },
    resetCurrentUser(state) {
      state.currentUser = {};
    },
    resetUserInfo(state) {
      state.userInfo = {};
    },
    setTempFormList(state, action) {
      state.tempFormList = action.payload;
    },
    setClaimList: (state, action) => {
      state.claimList = action.payload;
    },
    setNewClaimList: (state, action) => {
      state.newClaimList = action.payload;
    },
    setNewAddressList: (state, action) => {
      state.newAddressList = action.payload;
    },
    setDeletedClaims: (state, action) => {
      state.deletedClaims = action.payload;
    },
    setDeletedAddress: (state, action) => {
      state.deletedAddress = action.payload;
    },
    setSelectedUserId: (state, action) => {
      state.selectedUserId = action.payload;
    },
    setSelectedCustomerInfo: (state, action) => {
      state.selectedCustomerInfo = action.payload;
    },
    setAddressSelectionType(state, action) {
      state.selectedAddressType = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getCurrentUser.fulfilled, (state, action) => {
      const currentUser = action?.payload?.data?.hasOwnProperty('data')
        ? action?.payload?.data?.data
        : action?.payload?.data;
      state.currentUser = currentUser;
    });
    builder.addCase(getCurrentUser.rejected, (state) => {
      state.currentUser = {};
    });
    builder.addCase(currentUserUpdate.fulfilled, (state, action) => {
      const currentUser = action?.payload?.data?.hasOwnProperty('data')
        ? action?.payload?.data?.data
        : action?.payload?.data;
      state.currentUser = currentUser;
    });
    builder.addCase(reGmsSendOtp.fulfilled, (state, action) => {
      state.resGmsSendOtp = action?.payload?.data || [];
    });
    builder.addCase(reConfirmGmsOtp.fulfilled, (state, action) => {
      state.resConfirmGmsOtp = action?.payload?.data || [];
    });
    builder.addCase(reEmailSendOtp.fulfilled, (state, action) => {
      state.resEmailSendOtp = action?.payload?.data || [];
    });
    builder.addCase(reConfirmEmailOtp.fulfilled, (state, action) => {
      state.resConfirmEmailOtp = action?.payload?.data || [];
    });
    builder.addCase(userContactLookup.fulfilled, (state, action) => {
      state.contactList = action?.payload?.data || [];
    });
    builder.addCase(getUserInfo.fulfilled, (state, action) => {
      state.userInfo = action?.payload?.data || [];
    });
    builder.addCase(getUserAuthorizationStatus.fulfilled, (state, action) => {
      state.userAuthorizationStatus = action?.payload?.data || [];
    });
    builder.addCase(getUserAuthorizationStatusGift.fulfilled, (state, action) => {
      state.userAuthorizationGiftStatus = action?.payload?.data || [];
    });
    builder.addCase(getUserAuthorizationStatusMarket.fulfilled, (state, action) => {
      state.userAuthorizationMarketStatus = action?.payload?.data || [];
    });
    builder.addCase(getUserCheckedFunctions.fulfilled, (state, action) => {
      state.claims = {
        ...state.claims,
        [action?.meta?.arg?.body?.vouId]: {
          data: action?.payload?.data?.items || [],
          pagedProperty: action?.payload?.data?.pagedProperty,
        },
      };
      const vouId = action?.meta?.arg?.body?.vouId;
      // When a manual role switch (to a role without a dedicated auto-select flow)
      // is in progress, skip merging the backend's isCheck flags into claimList:
      // those flags still reflect the user's previously saved role, so applying
      // them here would re-tick claims that must start out unselected.
      if (!action?.meta?.arg?.skipCheckMerge) {
        const claims =
          action?.payload?.data?.items
            ?.map((claim) => {
              return claim?.isCheck && claim.id;
            })
            .filter((item) => item) || [];

        const tempClaim = claims.concat(
          state.claimList[vouId]?.filter((item) => claims.indexOf(item) < 0),
        );
        state.claimList = {
          ...state.claimList,
          [vouId]: tempClaim.filter((item) => item),
        };
      }

      state.newClaimList = {
        ...state.newClaimList,
        [vouId]: [...(state.newClaimList[vouId] || [])],
      };
      state.deletedClaims = {
        ...state.deletedClaims,
        [vouId]: [...(state.deletedClaims[vouId] || [])],
      };
    });
    builder.addCase(getListUserAddressLookup.fulfilled, (state, action) => {
      state.addressList = {
        ...state.addressList,
        [action?.meta?.arg?.body?.vouId]: {
          addressSelectionType: action?.payload?.data?.addressSelectionType,
          data: action?.payload?.data?.userAddresses || [],
          pagedProperty: {
            currentPage: action?.payload?.currentPage,
            hasNext: action?.payload?.hasNext,
            hasPrevious: action?.payload?.hasPrevious,
            pageSize: action?.payload?.pageSize,
            totalItems: action?.payload?.totalItems,
            totalPages: action?.payload?.totalPages,
          },
          searchText: action?.meta?.arg?.body?.searchText || null,
        },
      };
      const addresses = action?.payload?.data?.addressSelectionType
        ? action?.payload?.data?.userAddresses
          .map((item) => {
            return item.isChecked && item.addressId;
          })
          .filter((item) => item)
        : [];
      const vouId = action?.meta?.arg?.body?.vouId;
      const mergedList = addresses.concat(
        state.tempFormList[vouId]?.filter((item) => addresses.indexOf(item) < 0),
      );
      state.tempFormList = {
        ...state.tempFormList,
        [vouId]: mergedList.filter((item) => item),
      };
      state.newAddressList = {
        ...state.newAddressList,
        [vouId]: [...(state.newAddressList[vouId] || [])],
      };

      state.deletedAddress = {
        ...state.deletedAddress,
        [vouId]: [...(state.deletedAddress[vouId] || [])],
      };
    });
    builder.addCase(getCheckList.fulfilled, (state, action) => {
      const addressData = action?.payload?.data?.addresses || [];
      const claimData = action?.payload?.data?.groups || [];
      addressData.forEach((item) => {
        state.newAddressList = {
          ...state.newAddressList,
          [item.vouId]: item.ids.map((item) => item),
        };
      });
      claimData.forEach((item) => {
        state.newClaimList = {
          ...state.newClaimList,
          [item.vouId]: item.ids.map((item) => item),
        };
      });
    });
  },
});

export const {
  setSelectedUserId,
  resetCurrentUser,
  updateDetail,
  resetUserInfo,
  setTempFormList,
  setClaimList,
  setNewClaimList,
  setNewAddressList,
  setDeletedAddress,
  setDeletedClaims,
  setAddressSelectionType,
  setSelectedCustomerInfo,
} = userSlice.actions;
